// quantity.ts — reading an amount somebody typed.
//
// Two surfaces take one. On the recipe view a typed mass restates what you have
// of one ingredient and rescales the rest; in the editor's quantity column it
// becomes the mass the recipe stores. Both accept the same grammar, and it
// lives here so the two cannot drift into disagreeing about what "2 lb" is.
//
// What this file does *not* do is volume. A cup of flour and a cup of water are
// not the same mass, so "1 cup" is not a quantity until you know which food it
// is of — that conversion needs the density tables, and it belongs to the
// server (`measureQuantity` in api.ts), which has them and can say which row it
// used. Everything here is arithmetic that is true regardless of the food.

// The mass units, in grams. Deliberately no volumes: see above.
const MASS_UNITS: Record<string, number> = {
    mg: 0.001,
    g: 1, gram: 1, grams: 1,
    kg: 1000, kilo: 1000, kilos: 1000, kilogram: 1000, kilograms: 1000,
    oz: 28.349523125, ounce: 28.349523125, ounces: 28.349523125,
    lb: 453.59237, lbs: 453.59237, pound: 453.59237, pounds: 453.59237,
}

// parseMass reads a typed mass as grams, or null when the text is not one —
// which includes every half-finished one, and every amount that needs a food to
// mean anything ("1 cup", "3 cloves"). A bare number is grams.
//
// null is not a failure, it is a handoff: the recipe view treats it as "not a
// mass yet, keep typing", and the editor sends it to the server, which parses a
// far wider grammar than this — fractions, ranges, volumes, counts.
export function parseMass(text: string): number | null {
    const parts = /^\s*(\d*\.?\d+)\s*([a-z]*)\s*$/i.exec(text)
    if (!parts) return null
    const value = Number(parts[1])
    const factor = parts[2] === '' ? 1 : MASS_UNITS[parts[2].toLowerCase()]
    if (!Number.isFinite(value) || factor === undefined) return null
    return value * factor
}

// roundGrams drops the digits a conversion invents. 2 lb is 907.18474 g, and
// every one of those decimals is a claim about a bag of flour that no scale in
// a kitchen could check.
//
// It matches round2 in server/scrape/convert.go on purpose: the same typed
// amount has to settle to the same number whether the browser converted it or
// the server did, or the field would visibly change its mind about which units
// it trusts.
export function roundGrams(massG: number): number {
    return massG >= 10 ? Math.round(massG) : Math.round(massG * 10) / 10
}
